import { useUserContext } from '@/modules/user/useUserContext';
import { CLAIMS_LEVEL, type ClaimsLevel } from '@/modules/idv';
import {
  HelpText,
  UnlockFlow,
  extractBracketedText,
  stripBracketedText,
} from './useActionGuard';

export function useActionGuardContainer() {
  const {
    hasUserId,
    email: contextEmail,
    phone: contextPhone,
  } = useUserContext();

  const calculateHelpTextAndFlow = (
    claimsThatWouldUnlock: ClaimsLevel | null,
    helpText: HelpText
  ): [string, UnlockFlow] => {
    switch (claimsThatWouldUnlock) {
      case CLAIMS_LEVEL.PHONE:
        if (contextPhone && hasUserId) {
          return [
            helpText.singleVerification,
            UnlockFlow.PhoneVerificationModal,
          ];
        } else if (hasUserId) {
          return [helpText.updateProfile, UnlockFlow.ProfileWizardPhone];
        } else {
          return [helpText.setupProfile, UnlockFlow.ProfileWizardStart];
        }
      case CLAIMS_LEVEL.EMAIL:
        if (contextEmail && hasUserId) {
          return [
            helpText.singleVerification,
            UnlockFlow.EmailVerificationModal,
          ];
        } else if (hasUserId) {
          return [helpText.updateProfile, UnlockFlow.ProfileWizardEmail];
        } else if (contextPhone) {
          return [helpText.updateProfile, UnlockFlow.ProfileWizardPhone];
        } else {
          return [helpText.setupProfile, UnlockFlow.ProfileWizardStart];
        }
      case CLAIMS_LEVEL.PHONE_OR_EMAIL:
        if (contextPhone && hasUserId) {
          return [
            helpText.singleVerification,
            UnlockFlow.PhoneVerificationModal,
          ];
        } else if (contextEmail && hasUserId) {
          return [
            helpText.singleVerification,
            UnlockFlow.EmailVerificationModal,
          ];
        } else if (hasUserId) {
          return [helpText.updateProfile, UnlockFlow.ProfileWizardPhone];
        } else {
          return [helpText.setupProfile, UnlockFlow.ProfileWizardStart];
        }
      case CLAIMS_LEVEL.PHONE_AND_EMAIL:
        if (hasUserId) {
          return [helpText.updateProfile, UnlockFlow.ProfileWizardPhone];
        } else {
          return [helpText.setupProfile, UnlockFlow.ProfileWizardStart];
        }
      case null:
        return [
          hasUserId ? helpText.updateProfile : helpText.setupProfile,
          UnlockFlow.ProfileWizardStart,
        ];
      default:
        throw new Error(`Invalid claims level: ${claimsThatWouldUnlock}`);
    }
  };

  const calculateHelpText = (
    claimsThatWouldUnlock: ClaimsLevel | null,
    helpText: HelpText
  ): string => calculateHelpTextAndFlow(claimsThatWouldUnlock, helpText)[0];

  const calculateUnlockFlow = (
    claimsThatWouldUnlock: ClaimsLevel | null
  ): UnlockFlow =>
    calculateHelpTextAndFlow(claimsThatWouldUnlock, {
      singleVerification: '',
      updateProfile: '',
      setupProfile: '',
    })[1];

  return {
    calculateHelpTextAndFlow,
    calculateHelpText,
    calculateUnlockFlow,
    extractBracketedText,
    stripBracketedText,
  };
}
